import {
    getWeeksInYear,
    parsePeriodKey,
    RECONCILIATION_WEEK_COLOR,
} from 'features/ReconciliationReport/reconciliationWeekHelper';
import AppColors from 'general/constants/AppColors';
import useOnClickOutside from 'hooks/useOnClickOutside';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useMemo, useRef, useState } from 'react';

const WEEKS_PER_PAGE = 12;

CustomWeekPicker.propTypes = {
    value: PropTypes.shape({
        periodKey: PropTypes.string,
    }),
    onChange: PropTypes.func,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
};

function CustomWeekPicker({ value, onChange, placeholder = 'Chọn kỳ đối soát', disabled = false }) {
    const ref = useRef(null);
    const [show, setShow] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const selectedWeek = useMemo(() => parsePeriodKey(value?.periodKey), [value?.periodKey]);
    const [year, setYear] = useState(() => selectedWeek?.isoWeekYear || moment().isoWeekYear());
    const [weekPage, setWeekPage] = useState(0);

    const weeks = useMemo(() => getWeeksInYear(year), [year]);
    const totalWeekPages = Math.ceil(weeks.length / WEEKS_PER_PAGE);
    const visibleWeeks = useMemo(
        () => weeks.slice(weekPage * WEEKS_PER_PAGE, (weekPage + 1) * WEEKS_PER_PAGE),
        [weeks, weekPage]
    );

    useOnClickOutside(ref, () => {
        if (show) {
            setShow(false);
        }
    });

    const handleToggle = () => {
        if (disabled) {
            return;
        }
        if (!show && value?.periodKey) {
            const week = parsePeriodKey(value.periodKey);
            if (week) {
                setYear(week.isoWeekYear);
                setWeekPage(Math.floor((week.weekNum - 1) / WEEKS_PER_PAGE));
            }
        }
        setShow(!show);
    };

    const handleClear = (e) => {
        e.preventDefault();
        e.stopPropagation();
        onChange?.({ periodKey: '' });
        setShow(false);
    };

    const handleSelectWeek = (week) => {
        onChange?.({ periodKey: week.periodKey });
        setShow(false);
    };

    const handleChangeYear = (nextYear) => {
        setYear(nextYear);
        setWeekPage(0);
    };

    const showClearIcon = !!selectedWeek && isHovered && !disabled;

    return (
        <div
            ref={ref}
            className="position-relative w-100"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div
                role="button"
                tabIndex={disabled ? -1 : 0}
                className={`form-control d-flex align-items-center justify-content-between text-left ${disabled ? 'disabled' : 'cursor-pointer'}`}
                onClick={handleToggle}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleToggle();
                    }
                }}
            >
                {selectedWeek ? (
                    <span
                        style={{
                            color: AppColors.gray[800],
                            fontSize: 13,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {`Tuần ${String(selectedWeek.weekNum).padStart(2, '0')} (${moment(selectedWeek.from).format('DD/MM')} - ${moment(selectedWeek.to).format('DD/MM')})`}
                    </span>
                ) : (
                    <span style={{ color: AppColors.gray[500] }}>{placeholder}</span>
                )}
                {showClearIcon ? (
                    <i
                        className="fa-regular fa-circle-xmark flex-shrink-0 ml-2 cursor-pointer hover-opacity-60"
                        style={{ color: AppColors.gray[500], fontSize: 14 }}
                        onClick={handleClear}
                    />
                ) : (
                    <i className="fa-solid fa-calendar-days flex-shrink-0 ml-2" style={{ color: RECONCILIATION_WEEK_COLOR }} />
                )}
            </div>

            {show && (
                <div
                    className="position-absolute bg-white rounded shadow px-4 py-3 mt-1"
                    style={{
                        zIndex: 20,
                        minWidth: 480,
                        width: '100%',
                        border: `1px solid ${AppColors.gray[200]}`,
                    }}
                >
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <button
                            type="button"
                            className="btn p-0 border-0 bg-transparent"
                            style={{ color: RECONCILIATION_WEEK_COLOR }}
                            onClick={() => handleChangeYear(year - 1)}
                        >
                            <i className="fa-solid fa-angles-left" />
                        </button>
                        <span style={{ fontWeight: 600, color: RECONCILIATION_WEEK_COLOR, fontSize: 16 }}>{year}</span>
                        <button
                            type="button"
                            className="btn p-0 border-0 bg-transparent"
                            style={{ color: RECONCILIATION_WEEK_COLOR }}
                            onClick={() => handleChangeYear(year + 1)}
                        >
                            <i className="fa-solid fa-angles-right" />
                        </button>
                    </div>

                    <div
                        className="d-grid"
                        style={{
                            gridTemplateColumns: 'repeat(3, minmax(140px, 1fr))',
                            gap: 12,
                        }}
                    >
                        {visibleWeeks.map((week) => {
                            const isSelected = value?.periodKey === week.periodKey;
                            return (
                                <button
                                    key={week.periodKey}
                                    type="button"
                                    className="text-center p-2 border-0 bg-transparent"
                                    style={{
                                        color: isSelected ? AppColors.primary : RECONCILIATION_WEEK_COLOR,
                                        backgroundColor: 'transparent',
                                        borderRadius: 8,
                                        lineHeight: 1.35,
                                        textDecoration: 'none',
                                    }}
                                    onClick={() => handleSelectWeek(week)}
                                >
                                    <div
                                        style={{
                                            fontWeight: 600,
                                            fontSize: 14,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {`Tuần ${String(week.weekNum).padStart(2, '0')}`}
                                    </div>
                                    <div
                                        style={{
                                            fontSize: 12,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {`(${moment(week.from).format('DD/MM')} - ${moment(week.to).format('DD/MM')})`}
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    {totalWeekPages > 1 && (
                        <div className="d-flex align-items-center justify-content-center gap-3 mt-3">
                            <button
                                type="button"
                                className="btn p-0 border-0 bg-transparent"
                                style={{ color: RECONCILIATION_WEEK_COLOR }}
                                disabled={weekPage === 0}
                                onClick={() => setWeekPage((page) => Math.max(page - 1, 0))}
                            >
                                <i className="fa-solid fa-angle-left" />
                            </button>
                            <span style={{ fontSize: 12, color: RECONCILIATION_WEEK_COLOR }}>
                                {weekPage + 1}/{totalWeekPages}
                            </span>
                            <button
                                type="button"
                                className="btn p-0 border-0 bg-transparent"
                                style={{ color: RECONCILIATION_WEEK_COLOR }}
                                disabled={weekPage >= totalWeekPages - 1}
                                onClick={() => setWeekPage((page) => Math.min(page + 1, totalWeekPages - 1))}
                            >
                                <i className="fa-solid fa-angle-right" />
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default CustomWeekPicker;
